/**
 * Turning `SessionState` into the handful of questions the UI actually asks.
 *
 * The fifteen UX states in the brief are not fifteen mutually exclusive screens — a
 * session can be simultaneously low-confidence, waiting on branches, and offline. So
 * this derives a set of orthogonal flags rather than a single enum, and the components
 * compose them. Pretending they were exclusive is how you end up hiding the offline chip
 * because the chord happened to be ambiguous.
 *
 * Pure. Given the same state it returns the same phase, which is what makes the fixture
 * switcher an honest preview rather than an approximation.
 *
 * It reads `view.mode` as well as `view.state`, because where the player is changes which
 * questions are even askable. The reducer promotes `listening` to `'active'` on the first
 * appended chord whether that chord was played or tapped, so `state.listening` alone can
 * never be the thing microphone chrome keys off.
 */

import {
  currentBranchSet,
  currentNode,
  type BranchSet,
  type DetectedChord,
  type SessionNode,
  type SessionState,
} from '@hc/shared';
import type { SessionView } from './sessionView.js';

/**
 * Below this, the reading gets a "possibly…" line.
 *
 * Set by ear against the recogniser's scoring rather than derived: it is the point at
 * which a wrong reading stops being surprising, not a statistical threshold.
 */
export const LOW_CONFIDENCE_BELOW = 0.62;

/**
 * When the runner-up scores within this of the best, the reading is genuinely ambiguous
 * — two defensible hearings of the same sound, not one good one and one bad one. That is
 * a different statement from "low confidence" and it gets different chrome.
 */
export const AMBIGUITY_MARGIN = 0.08;

/** How the current reading should be presented. */
export type ReadingQuality =
  /** Full strength, no chrome whatsoever. */
  | 'confident'
  /** Add the tappable "possibly …" line. */
  | 'uncertain'
  /** Dotted underline; long-press reveals both readings. */
  | 'ambiguous'
  /** Nothing has been heard yet. */
  | 'none';

export type BranchPhase = 'idle' | 'loading' | 'visible' | 'unavailable';

export interface SessionPhase {
  screen: 'unsupported' | 'permission' | 'home' | 'session';
  /** Overlays whichever screen is showing. */
  showResumeCard: boolean;
  /** Listening, nothing stable yet — the breathing dot. */
  awaitingFirstChord: boolean;
  /**
   * The On the Go twin of `awaitingFirstChord`: a session has begun and nothing has been
   * chosen yet. Deliberately a second flag rather than a mode check at every call site,
   * because the two states want opposite chrome — one is the app waiting on a room, the
   * other is the app waiting on a tap, and a breathing microphone dot on a train is a lie.
   */
  chooseFirstChord: boolean;
  current: SessionNode | null;
  detected: DetectedChord | null;
  reading: ReadingQuality;
  branches: BranchPhase;
  branchSet: BranchSet | null;
  /** The set offered from the previous node. Stays faded behind skeletons. */
  previousBranchSet: BranchSet | null;
  /** The current node was reached by playing something that was offered. */
  arrivedBySuggestion: boolean;
  /** The current node was reached by playing something that was not. Not an error. */
  arrivedOffPath: boolean;
  /** The LLM is unreachable; everything else still works. */
  companionOffline: boolean;
  showSaveToast: boolean;
}

/** Whether two readings of the same sound are close enough to both be defensible. */
export function isAmbiguousReading(detected: DetectedChord | undefined | null): boolean {
  if (!detected) return false;
  const runnerUp = detected.alternatives[0];
  if (!runnerUp) return false;
  return detected.best.score - runnerUp.score <= AMBIGUITY_MARGIN;
}

function readingQuality(node: SessionNode | null): ReadingQuality {
  if (!node) return 'none';
  // A chord the player typed or corrected is not a reading at all — it is a statement.
  // Showing "possibly" over the top of someone's own correction would be insulting.
  if (node.source !== 'audio' || !node.detected) return 'confident';
  if (isAmbiguousReading(node.detected)) return 'ambiguous';
  if (node.detected.confidence < LOW_CONFIDENCE_BELOW) return 'uncertain';
  return 'confident';
}

function branchPhase(set: BranchSet | null): BranchPhase {
  if (!set) return 'idle';
  switch (set.status) {
    case 'loading':
      return 'loading';
    case 'failed':
      return 'unavailable';
    case 'partial':
    case 'ready':
    case 'fallback':
      return set.suggestions.length > 0 ? 'visible' : 'loading';
    default:
      return 'idle';
  }
}

/** The branch set belonging to the node immediately before the current one. */
function previousBranchSetOf(state: SessionState): BranchSet | null {
  const index = state.path.findIndex((node) => node.id === state.currentNodeId);
  if (index <= 0) return null;
  const previous = state.path[index - 1];
  if (!previous) return null;
  return state.branchSets[previous.id] ?? null;
}

/**
 * Which screen to show.
 *
 * At the piano the microphone is the gate, so `micStatus` is the whole answer. On the Go
 * there is no microphone to have an opinion about: the only question is whether a session
 * has begun. That is why this cannot be one expression with a mode flag threaded through
 * it — a browser with no `AudioWorklet` is *unsupported for listening* and perfectly
 * capable of everything else, and a denied permission is not a fact about a train.
 */
function screenFor(view: SessionView, onTheGo: boolean): SessionPhase['screen'] {
  const { state } = view;
  if (onTheGo) {
    return state.path.length > 0 || state.listening !== 'idle' ? 'session' : 'home';
  }
  const capabilities = view.capabilities;
  if (capabilities && !capabilities.supported) return 'unsupported';
  if (state.micStatus === 'denied' || state.micStatus === 'unavailable') return 'permission';
  // An unrequested microphone normally means nothing has happened yet — but a restored
  // or continued session arrives exactly this way (`sanitizeRestoredState` and
  // `sessionStateFromSketch` both reset micStatus, because a fresh page load cannot vouch
  // for a stream it does not hold). A non-empty path is a session, and hiding it behind
  // the home screen — whose only button starts over — destroys it. The session screen
  // offers "tap to start listening" instead (StatusChips).
  return state.micStatus === 'unrequested' && state.path.length === 0 ? 'home' : 'session';
}

export function deriveSessionPhase(view: SessionView): SessionPhase {
  const { state } = view;
  const node = currentNode(state);
  const set = currentBranchSet(state);
  const onTheGo = view.mode === 'on-the-go';
  const awaiting = node === null && state.listening !== 'idle';

  return {
    screen: screenFor(view, onTheGo),
    showResumeCard: Boolean(view.resumeSnapshot),
    awaitingFirstChord: !onTheGo && awaiting,
    chooseFirstChord: onTheGo && awaiting,
    current: node,
    detected: node?.detected ?? null,
    reading: readingQuality(node),
    branches: branchPhase(set),
    branchSet: set,
    previousBranchSet: previousBranchSetOf(state),
    arrivedBySuggestion: Boolean(node?.fromSuggestionId),
    arrivedOffPath: Boolean(node && !node.fromSuggestionId && state.path.length > 1),
    /*
     * There is no companion to be offline from when the companion is switched off.
     *
     * Nothing on the built-in engine's path can dispatch `BRANCHES_FAILED`, so the flag
     * cannot go false while it is answering — but it can already *be* false, left over
     * from a stretch with the model on, and a chip reporting a network the app has
     * stopped using would be reporting nothing. `!== false` rather than `=== true`: a
     * view that never described its settings has not said the companion is off, and a
     * fixture of an offline session still draws the chip it exists to show.
     */
    companionOffline: !state.llmHealthy && view.settings?.aiCompanion !== false,
    showSaveToast: Boolean(view.lastSave),
  };
}
